import React, { useEffect, useState } from 'react'
import { deviceDetect } from 'react-device-detect'
import { Layout, Row, Col, Modal, notification } from 'antd'
import SearchRefinement from './components/SearchRefinement'
import NavigationBar from './components/NavigationBar'
import SearchResults from './components/SearchResults'
import DoctorAvailabilityView from './components/DoctorAvailabilityView'
import MobileSearchEngine from './components/Mobile'
import { SearchProvider } from './contexts/SearchContext'
import { BookingProvider } from './contexts/BookingContext'
import { useSearchRedux, useBookingRedux } from './hooks/reduxHooks'

const { Content } = Layout

// Main search engine wrapper with providers
const SearchEngineWithProviders = ({ isVirtualVisit = false }) => {
  // Check if mobile device
  if (deviceDetect().isMobile) {
    return (
      <SearchProvider>
        <BookingProvider>
          <MobileSearchEngine isVirtualVisit={isVirtualVisit} />
        </BookingProvider>
      </SearchProvider>
    )
  }

  // Desktop version
  return (
    <SearchProvider>
      <BookingProvider>
        <SearchEngine />
      </BookingProvider>
    </SearchProvider>
  )
}

// Core search engine component
const SearchEngine = () => {
  // Redux hooks
  const {
    searchFilters,
    searchResults,
    loading,
    error,
    pagination,
    performSearch,
    changePage,
    updateFilter,
    clearError
  } = useSearchRedux()
  const searchType = searchFilters.searchType || 'general_practitioner'
  console.log('Current searchType from Redux:', searchType, 'Full searchFilters:', searchFilters)
  const {
    selectedDoctor,
    showAvailabilityView,
    openAvailabilityView,
    closeAvailabilityView,
    bookingSuccess,
    bookingError,
    clearBookingError
  } = useBookingRedux()

  // Handle search with error handling
  const handleSearch = async (newFilters) => {
    try {
      await performSearch(newFilters)
    } catch (err) {
      notification.error({
        message: 'Search Failed',
        description: err.message || 'Unable to perform search. Please try again.',
        duration: 4
      })
    }
  }

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    updateFilter(filterType, value)
  }

  // Handle searchType changes
  const setSearchType = (newSearchType) => {
    console.log('Setting searchType in main component:', newSearchType)
    updateFilter('searchType', newSearchType)
  }

  // Handle page changes with error handling
  const handlePageChange = async (page) => {
    try {
      await changePage(page)
    } catch (err) {
      notification.error({
        message: 'Page Navigation Failed',
        description: err.message || 'Unable to load page. Please try again.',
        duration: 4
      })
    }
  }

  // Handle showing availability view
  const handleViewAvailability = (doctor) => {
    openAvailabilityView(doctor)
  }

  // Handle closing availability view
  const handleCloseAvailability = () => {
    closeAvailabilityView()
  }

  // Handle appointment booking success
  const handleBookAppointment = (appointmentData) => {
    Modal.success({
      title: 'Appointment Booked Successfully!',
      content: (
        <div>
          <p>
            <strong>Doctor:</strong> {appointmentData.doctor?.firstName} {appointmentData.doctor?.lastName}
          </p>
          <p>
            <strong>Date & Time:</strong> {appointmentData.datetime}
          </p>
          <p>
            <strong>Reason:</strong> {appointmentData.reason}
          </p>
          <p>
            <strong>Clinic:</strong> {appointmentData.clinic?.name}
          </p>
        </div>
      ),
      onOk: () => {
        closeAvailabilityView()
      }
    })
  }

  // Handle errors
  useEffect(() => {
    if (error) {
      notification.error({
        message: 'Error',
        description: error,
        duration: 4,
        onClose: clearError
      })
    }
  }, [error, clearError])

  useEffect(() => {
    if (bookingError) {
      notification.error({
        message: 'Booking Error',
        description: bookingError,
        duration: 4,
        onClose: clearBookingError
      })
    }
  }, [bookingError, clearBookingError])

  // Show booking success
  useEffect(() => {
    if (bookingSuccess) {
      notification.success({
        message: 'Booking Confirmed',
        description: 'Your appointment has been successfully booked!',
        duration: 4
      })
    }
  }, [bookingSuccess])

  // Initial search on component mount
  useEffect(() => {
    if (searchResults.length === 0 && !loading) {
      // Use complete searchFilters from Redux state for initial search
      performSearch(searchFilters)
    }
  }, []) // Only run on mount

  // Show availability view full screen
  if (showAvailabilityView && selectedDoctor) {
    return (
      <Layout
        style={{
          minHeight: '100vh',
          background: '#f5f5f5'
        }}
      >
        <Content>
          <DoctorAvailabilityView
            doctor={selectedDoctor}
            onClose={handleCloseAvailability}
            onBookAppointment={handleBookAppointment}
          />
        </Content>
      </Layout>
    )
  }

  // Show search interface
  return (
    <Layout
      style={{
        minHeight: '100vh',
        background: '#f9fafb'
      }}
    >
      <NavigationBar
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        totalResults={pagination.totalResults}
        resultsPerPage={pagination.resultsPerPage}
        onPageChange={handlePageChange}
        loading={loading}
        searchType={searchType}
      />

      <Content>
        <div
          style={{
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '20px'
          }}
        >
          <Row gutter={[24, 24]}>
            <Col
              xs={24}
              lg={8}
              xl={6}
              style={{
                position: { lg: 'sticky' },
                top: { lg: '20px' },
                height: { lg: 'fit-content' }
              }}
            >
              <SearchRefinement
                searchType={searchType}
                setSearchType={setSearchType}
                onSearch={handleSearch}
                onFilterChange={handleFilterChange}
                searchFilters={searchFilters}
                loading={loading}
              />
            </Col>

            <Col xs={24} lg={16} xl={18}>
              <SearchResults
                searchType={searchType}
                results={searchResults}
                loading={loading}
                onViewAvailability={handleViewAvailability}
                error={error}
                totalResults={pagination.totalResults}
                searchFilters={searchFilters}
              />
            </Col>
          </Row>
        </div>
      </Content>
    </Layout>
  )
}

export default SearchEngineWithProviders
